import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { ApiUrl } from 'Services/Services';

const ITEMS_PER_PAGE = 5;

function Categories() {
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [newCategory, setNewCategory] = useState({
    title: '',
    description: '',
    image: null,
  });

  const totalPages = Math.ceil(categories.length / ITEMS_PER_PAGE);
  const paginatedData = categories.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const fetchCategory = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/category`);
      setCategories(response.data.data);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  const handleModalChange = (e) => {
    setNewCategory({ ...newCategory, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewCategory((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleAddCategory = async () => {
    try {
      const formData = new FormData();
      formData.append('title', newCategory.title);
      formData.append('description', newCategory.description);
      formData.append('image', newCategory.image);

      await axios.post(`${ApiUrl}/category`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Category Created');
      resetModal();
      fetchCategory();
    } catch (error) {
      console.error('API error:', error.message);
      toast.warn(error.message);
    }
  };

  const handleUpdateCategory = async () => {
    try {
      const formData = new FormData();
      formData.append('title', newCategory.title);
      formData.append('description', newCategory.description);
      if (newCategory.image) {
        formData.append('image', newCategory.image);
      }

      await axios.put(`${ApiUrl}/category/update/${editingCategoryId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Category Updated');
      resetModal();
      fetchCategory();
    } catch (error) {
      console.error('Update error:', error.message);
      toast.warn(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${ApiUrl}/category/delete/${id}`);
      fetchCategory();
      toast.success('Category Deleted');
    } catch (error) {
      console.log(error.message);
      toast.warn(error.message);
    }
  };

  const handleEdit = (id) => {
    const category = categories.find((cat) => cat.id === id);
    if (category) {
      setNewCategory({
        title: category.title,
        description: category.description,
        image: null,
      });
      setEditingCategoryId(id);
      setIsEditing(true);
      setIsModalOpen(true);
    }
  };

  const resetModal = () => {
    setNewCategory({ title: '', description: '', image: null });
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingCategoryId(null);
  };

  return (
    <div className="p-6">
      {/* Add Category Button */}
      <div className="flex justify-end pb-5">
        <button
          onClick={() => {
            setIsEditing(false);
            setNewCategory({ title: '', description: '', image: null });
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Add Category
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-4">
              {isEditing ? 'Edit Category' : 'Add Category'}
            </h2>
            <div className="space-y-4">
              <input
                name="title"
                placeholder="Title"
                value={newCategory.title}
                onChange={handleModalChange}
                className="w-full border border-gray-300 px-4 py-2 rounded-md focus:outline-none focus:ring focus:ring-blue-300"
              />
              <input
                name="description"
                placeholder="Description"
                value={newCategory.description}
                onChange={handleModalChange}
                className="w-full border border-gray-300 px-4 py-2 rounded-md focus:outline-none focus:ring focus:ring-blue-300"
              />
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full border border-gray-300 px-4 py-2 rounded-md focus:outline-none focus:ring focus:ring-blue-300"
              />

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={resetModal}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                {isEditing ? (
                  <button
                    onClick={handleUpdateCategory}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Update
                  </button>
                ) : (
                  <button
                    onClick={handleAddCategory}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Table */}
      <div className="overflow-x-auto bg-white shadow-md rounded-lg">
        <table className="min-w-full table-auto text-left text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3">Image</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((cat) => (
              <tr key={cat.id} className="border-b">
                <td className="px-4 py-3">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="w-12 h-12 rounded-md object-cover"
                  />
                </td>
                <td className="px-4 py-3">{cat.title}</td>
                <td className="px-4 py-3">{cat.description}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button
                    onClick={() => handleEdit(cat.id)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-end items-center gap-4">
        <button
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-sm">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Categories;
